import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APIRequestResources, CachedAPIRequest, LoadingService, PaginationResponse } from 'src/app/core';
import { BehaviorSubject, finalize, Observable, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { CalloutFindParams, CalloutFindResult, CalloutSave, CalloutStatus, CalloutUpdate, OptionItem } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class CallOutService extends CachedAPIRequest {
  private loading = inject(LoadingService);

  private $callOuts = new BehaviorSubject<CalloutFindResult[]>([]);
  public callOuts = toSignal(this.$callOuts, { initialValue: [] });

  private $totalItems = new BehaviorSubject<number>(0);
  public totalItems = toSignal(this.$totalItems, { initialValue: 0 });

  constructor(protected override http: HttpClient) {
    super(http, APIRequestResources.CalloutService);
  }

  readonly calloutStatuses: OptionItem[] = [
    { id: 1, name: 'Pending', code: 'pending' },
    { id: 2, name: 'Approved', code: 'approved' },
    { id: 3, name: 'Rejected', code: 'rejected' },
    { id: 4, name: 'Cancelled', code: 'cancelled' },
    { id: 5, name: 'Delivered', code: 'delivered' }
  ];

  readonly productTypes: OptionItem[] = [
    { id: 1, name: 'Mono Carton', code: 'mono_carton' },
    { id: 2, name: 'Corrugated', code: 'corrugated' },
    { id: 3, name: 'Label', code: 'label' },
    { id: 4, name: 'Commercial', code: 'commercial' }
  ];

  findCallout(searchParams: CalloutFindParams, refresh = true): Observable<any> {
    this.loading.set(true, { immediate: true });
    return this.get<PaginationResponse<CalloutFindResult[]>>(
      {
        endpoint: 'find',
        params: searchParams
      },
      refresh ? 'freshness' : 'performance'
    ).pipe(
      tap((res) => {
        const data = res?.data?.data || [];
        this.$callOuts.next(data);
        this.$totalItems.next(res?.data?.totalItems || 0);
      }),
      finalize(() => {
        this.loading.set(false);
      })
    );
  }

  updateCallout(payload: CalloutUpdate, id: string): Observable<any> {
    this.loading.set(true, { immediate: true });
    return this.patch<any>(payload, { suffix: `${id}` }).pipe(
      finalize(() => {
        this.loading.set(false);
      })
    );
  }

  updateStatus(id: string | number, status: CalloutStatus): Observable<any> {
    this.loading.set(true, { immediate: true });
    return this.patch<any>({ status }, { suffix: `status/${id}` }).pipe(
      finalize(() => {
        this.loading.set(false);
      })
    );
  }

  createCallout(payload: CalloutSave): Observable<any> {
    this.loading.set(true, { immediate: true });
    return this.post<any>(payload, {}).pipe(
      finalize(() => {
        this.loading.set(false);
      })
    );
  }
}
